<template>
	<app-page>
		<app-heading title="Promo Code Usage Report" />
		<app-row>
			<app-cell cols="3">
				<input-date-picker
					v-model="date"
					label="Date"
					type="month"
				/>
			</app-cell>
			<app-cell cols="3">
				<app-button
					:disabled="!date || loading"
					:loading="loading"
					@click="fetchPromoCodeReport"
				>
					Fetch Report
				</app-button>
			</app-cell>
		</app-row>

		<!-- Promo Code Reports -->
		<div v-if="reportData">
			<div v-for="promoData in reportData.promoCodeSections" :key="promoData.promoCode">
				<!-- Promo Code Title -->
				<app-row>
					<app-cell>
						<v-card>
							<v-card-title class="text-h5 primary--text">
								{{ promoData.promoCode }}
							</v-card-title>
						</v-card>
					</app-cell>
				</app-row>

				<!-- Overall Totals for this Promo Code -->
				<app-row>
					<app-cell>
						<v-card>
							<v-card-title>Overall Totals</v-card-title>
							<v-card-text>
								<app-row>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h5 success--text">{{ promoData.overall.converted }}</div>
											<div class="text-subtitle-2">Converted</div>
										</div>
									</app-cell>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h6 success--text">
												${{ promoData.overall.convertedAmount.toLocaleString() }}
											</div>
											<div class="text-subtitle-2">Converted Amount</div>
										</div>
									</app-cell>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h5 error--text">{{ promoData.overall.cancelled }}</div>
											<div class="text-subtitle-2">Cancelled</div>
										</div>
									</app-cell>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h5 info--text">{{ promoData.overall.traveled }}</div>
											<div class="text-subtitle-2">Traveled</div>
										</div>
									</app-cell>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h6 info--text">
												${{ promoData.overall.traveledAmount.toLocaleString() }}
											</div>
											<div class="text-subtitle-2">Traveled Amount</div>
										</div>
									</app-cell>
									<app-cell cols="6" md="2">
										<div class="text-center">
											<div class="text-h6 grey--text">{{ promoData.overall.websites }}</div>
											<div class="text-subtitle-2">Unique Websites</div>
										</div>
									</app-cell>
								</app-row>
							</v-card-text>
						</v-card>
					</app-cell>
				</app-row>

				<!-- By Website Report for this Promo Code -->
				<app-row>
					<app-cell>
						<v-card>
							<v-card-title>Bookings by Website</v-card-title>
							<v-card-text>
							<v-data-table
								:items="promoData.byWebsite"
								:headers="websiteHeaders"
								:sort-by="'convertedAmount'"
								:sort-desc="true"
								hide-default-footer
								disable-pagination
							>
								<template v-slot:item.converted="{ item }">
									<span class="success--text font-weight-bold">{{ item.converted }}</span>
								</template>
								<template v-slot:item.convertedAmount="{ item }">
									<span class="success--text">
										${{ item.convertedAmount.toLocaleString() }}
									</span>
								</template>
								<template v-slot:item.cancelled="{ item }">
									<span class="error--text font-weight-bold">{{ item.cancelled }}</span>
								</template>
								<template v-slot:item.traveled="{ item }">
									<span class="info--text font-weight-bold">{{ item.traveled }}</span>
								</template>
								<template v-slot:item.traveledAmount="{ item }">
									<span class="info--text">
										${{ item.traveledAmount.toLocaleString() }}
									</span>
								</template>
							</v-data-table>
							</v-card-text>
						</v-card>
					</app-cell>
				</app-row>

				<!-- By Resort Report for this Promo Code -->
				<app-row>
					<app-cell>
						<v-card>
							<v-card-title>Bookings by Resort</v-card-title>
							<v-card-text>
							<v-data-table
								:items="promoData.byResort"
								:headers="resortHeaders"
								:sort-by="'convertedAmount'"
								:sort-desc="true"
								hide-default-footer
								disable-pagination
							>
								<template v-slot:item.converted="{ item }">
									<span class="success--text font-weight-bold">{{ item.converted }}</span>
								</template>
								<template v-slot:item.convertedAmount="{ item }">
									<span class="success--text">
										${{ item.convertedAmount.toLocaleString() }}
									</span>
								</template>
								<template v-slot:item.cancelled="{ item }">
									<span class="error--text font-weight-bold">{{ item.cancelled }}</span>
								</template>
								<template v-slot:item.traveled="{ item }">
									<span class="info--text font-weight-bold">{{ item.traveled }}</span>
								</template>
								<template v-slot:item.traveledAmount="{ item }">
									<span class="info--text">
										${{ item.traveledAmount.toLocaleString() }}
									</span>
								</template>
							</v-data-table>
							</v-card-text>
						</v-card>
					</app-cell>
				</app-row>

				<!-- Spacer between promo codes -->
				<app-row v-if="promoData !== reportData.promoCodeSections[reportData.promoCodeSections.length - 1]">
					<app-cell>
						<div style="height: 32px;"></div>
					</app-cell>
				</app-row>
			</div>
		</div>
	</app-page>
</template>

<script>
import moment from 'moment'

export default {
	async created() {
		// Load resorts for resort name mapping
		this.resorts = await this.$ravello.request()
			.v(2)
			.to('resorts')
			.fields(['name', 'studeo_id'])
			.limit(100)
			.get()

		this.resortsMap = {}
		this.resorts.forEach((resort) => {
			this.resortsMap[resort.studeo_id.toString()] = resort.name
		})
	},
	data() {
		return {
			date: null,
			loading: false,
			reportData: null,
			resorts: [],
			resortsMap: {},
			websiteHeaders: [
				{
					text: 'Website',
					value: 'website',
					sortable: true,
				},
				{
					text: 'Converted',
					value: 'converted',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Converted Amount',
					value: 'convertedAmount',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Cancelled',
					value: 'cancelled',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Traveled',
					value: 'traveled',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Traveled Amount',
					value: 'traveledAmount',
					sortable: true,
					align: 'center',
				},
			],
			resortHeaders: [
				{
					text: 'Resort',
					value: 'resortName',
					sortable: true,
				},
				{
					text: 'Converted',
					value: 'converted',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Converted Amount',
					value: 'convertedAmount',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Cancelled',
					value: 'cancelled',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Traveled',
					value: 'traveled',
					sortable: true,
					align: 'center',
				},
				{
					text: 'Traveled Amount',
					value: 'traveledAmount',
					sortable: true,
					align: 'center',
				},
			],
		}
	},
	methods: {
		async getIntegrationId() {
			const [integration] = await this.$ravello.request()
				.v(2)
				.to('integrations')
				.param('slug', 'website')
				.fields(['_id'])
				.get()

			return integration && integration._id
		},

		/**
		 * Fetches bookings by created for new/updated bookings
		 * @param {string} integrationId - Integration ID
		 * @param {string} startDate - Start date
		 * @param {string} endDate - End date
		 * @returns {Promise<Array>} Created bookings
		 */
		async fetchCreatedBookings(integrationId, startDate, endDate) {
			return this.$ravello.request()
				.v(2)
				.to('bookings')
				.param('integration', integrationId)
				.param('created', startDate, 'gte')
				.param('created', endDate, 'lte')
				.param('last_action', ['new', 'update'])
				.fields([
					'content.promo_code',
					'content.website',
					'studeo_resort',
					'last_action',
					'created',
					'amount.total',
				])
				.limit(10000)
				.get()
		},

		/**
		 * Fetches bookings by reservation.checkout for new/updated bookings (Traveled)
		 * @param {string} integrationId - Integration ID
		 * @param {string} startDate - Start date
		 * @param {string} endDate - End date
		 * @returns {Promise<Array>} Checkout date bookings
		 */
		async fetchCheckoutBookings(integrationId, startDate, endDate) {
			return this.$ravello.request()
				.v(2)
				.to('bookings')
				.param('integration', integrationId)
				.param('reservation.checkout', startDate, 'gte')
				.param('reservation.checkout', endDate, 'lte')
				.param('last_action', ['new', 'update'])
				.fields([
					'content.promo_code',
					'content.website',
					'reservation.checkout',
					'studeo_resort',
					'last_action',
					'amount.total',
				])
				.limit(10000)
				.get()
		},

		/**
		 * Fetches cancelled bookings by last_modified
		 * @param {string} integrationId - Integration ID
		 * @param {string} startDate - Start date
		 * @param {string} endDate - End date
		 * @returns {Promise<Array>} Cancelled bookings
		 */
		async fetchCancelledBookings(integrationId, startDate, endDate) {
			return this.$ravello.request()
				.v(2)
				.to('bookings')
				.param('integration', integrationId)
				.param('last_modified', startDate, 'gte')
				.param('last_modified', endDate, 'lte')
				.param('last_action', 'cancel')
				.fields([
					'content.promo_code',
					'content.website',
					'studeo_resort',
					'last_action',
					'last_modified',
				])
				.limit(10000)
				.get()
		},

		async fetchPromoCodeReport() {
			this.loading = true
			this.reportData = null

			try {
				const startDate = `${this.date}-01`
				const endDate = moment(startDate).endOf('month').format('YYYY-MM-DD')

				const integrationId = await this.getIntegrationId()
				if (!integrationId) {
					throw new Error('Website integration not found')
				}

				// Execute all three queries in parallel
				const [createdBookings, checkoutBookings, cancelledBookings] = await Promise.all([
					this.fetchCreatedBookings(integrationId, startDate, endDate),
					this.fetchCheckoutBookings(integrationId, startDate, endDate),
					this.fetchCancelledBookings(integrationId, startDate, endDate),
				])

				this.reportData = this.compileReportData({
					createdBookings,
					checkoutBookings,
					cancelledBookings,
				})

				const totalBookings = createdBookings.length + checkoutBookings.length + cancelledBookings.length
				this.$notifications.pushAlert('success', `Report compiled successfully with ${totalBookings} bookings`)
			} catch (err) {
				this.$notifications.pushAlert('error', (err.data && err.data.message) || err.message)
			} finally {
				this.loading = false
			}
		},

		/**
		 * Compiles booking data from three sources into a structured report grouped by promo code
		 * @param {Object} bookingData - Object containing three arrays of booking data
		 * @returns {Object} Compiled report data with separate sections per promo code
		 */
		compileReportData(bookingData) {
			// First, group all bookings by promo code
			const promoCodeGroups = {}

			// Helper function to process bookings and group them by promo code
			const processBookingsByPromoCode = (bookings, bookingType) => {
				bookings.forEach((booking) => {
					const promoCode = (booking.content && booking.content.promo_code) || 'No Promo Code'

					if (!promoCodeGroups[promoCode]) {
						promoCodeGroups[promoCode] = {
							createdBookings: [],
							checkoutBookings: [],
							cancelledBookings: [],
						}
					}

					promoCodeGroups[promoCode][bookingType].push(booking)
				})
			}

			// Group bookings by promo code
			processBookingsByPromoCode(bookingData.createdBookings, 'createdBookings')
			processBookingsByPromoCode(bookingData.checkoutBookings, 'checkoutBookings')
			processBookingsByPromoCode(bookingData.cancelledBookings, 'cancelledBookings')

			// Now create sections for each promo code with the same structure as before
			const promoCodeSections = Object.keys(promoCodeGroups).map((promoCode) => {
				const promoBookings = promoCodeGroups[promoCode]
				return this.compilePromoCodeSection(promoCode, promoBookings)
			})

			// Sort by converted amount descending
			promoCodeSections.sort((a, b) => b.overall.convertedAmount - a.overall.convertedAmount)

			return {
				promoCodeSections,
			}
		},

		/**
		 * Compiles a single promo code section with overall, website, and resort breakdowns
		 * @param {string} promoCode - The promo code
		 * @param {Object} bookingData - Booking data for this promo code
		 * @returns {Object} Complete section data for this promo code
		 */
		compilePromoCodeSection(promoCode, bookingData) {
			const section = {
				promoCode,
				overall: {
					converted: 0,
					convertedAmount: 0,
					cancelled: 0,
					traveled: 0,
					traveledAmount: 0,
					websites: 0,
				},
				byWebsite: {},
				byResort: {},
			}

			// Helper function to process bookings for this promo code
			const processBookings = (bookings, bookingType) => {
				bookings.forEach((booking) => {
					const website = (booking.content && booking.content.website) || 'Unknown Website'
					const resortId = booking.studeo_resort && booking.studeo_resort.toString()
					const resortName = this.resortsMap[resortId] || (resortId ? `Resort ${resortId}` : 'Unknown Resort')
					const bookingAmount = (booking.amount && booking.amount.total) || 0

					// Update overall totals
					section.overall[bookingType]++

					// Add amount for converted and traveled bookings
					if (bookingType === 'converted') {
						section.overall.convertedAmount += bookingAmount
					} else if (bookingType === 'traveled') {
						section.overall.traveledAmount += bookingAmount
					}

					// Group by website
					if (!section.byWebsite[website]) {
						section.byWebsite[website] = {
							website,
							converted: 0,
							convertedAmount: 0,
							cancelled: 0,
							traveled: 0,
							traveledAmount: 0,
						}
					}
					section.byWebsite[website][bookingType]++

					// Add amounts to website grouping
					if (bookingType === 'converted') {
						section.byWebsite[website].convertedAmount += bookingAmount
					} else if (bookingType === 'traveled') {
						section.byWebsite[website].traveledAmount += bookingAmount
					}

					// Group by resort
					if (!section.byResort[resortId]) {
						section.byResort[resortId] = {
							resortId,
							resortName,
							converted: 0,
							convertedAmount: 0,
							cancelled: 0,
							traveled: 0,
							traveledAmount: 0,
						}
					}
					section.byResort[resortId][bookingType]++

					// Add amounts to resort grouping
					if (bookingType === 'converted') {
						section.byResort[resortId].convertedAmount += bookingAmount
					} else if (bookingType === 'traveled') {
						section.byResort[resortId].traveledAmount += bookingAmount
					}
				})
			}

			// Process each category of bookings for this promo code
			processBookings(bookingData.createdBookings, 'converted')
			processBookings(bookingData.checkoutBookings, 'traveled')
			processBookings(bookingData.cancelledBookings, 'cancelled')

			// Calculate unique websites count
			section.overall.websites = Object.keys(section.byWebsite).length

			// Convert grouped objects to arrays for table display
			section.byWebsite = Object.keys(section.byWebsite)
				.map(key => section.byWebsite[key])
				.sort((a, b) => b.convertedAmount - a.convertedAmount) // Sort by converted amount descending

			section.byResort = Object.keys(section.byResort)
				.map(key => section.byResort[key])
				.sort((a, b) => b.convertedAmount - a.convertedAmount) // Sort by converted amount descending

			return section
		},
	},
}
</script>
